import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Search, CalendarCheck, Video, FileText, ShieldCheck, Clock, Wallet, Upload, FlaskConical, HeartPulse, Microscope, Stethoscope } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PublicShell } from "@/components/PublicLayout";
import { SearchFilters, emptyFilters } from "@/components/DoctorDirectory";
import { api } from "@/lib/api";
import { FluidBackdrop, Reveal } from "@/components/Extras";
import { AiShowcase } from "@/components/AiShowcase";
import { JointPromo } from "@/components/JointPromo";
import { TypeHeadline, SuvidhaPromo } from "@/components/LandingExtras";
import { LabBooking, PartnerLogoStrip } from "@/components/LabBooking";
import { Pill, ScanLine, FolderHeart, RefreshCcw, Store, Smartphone } from "lucide-react";

const SERVICES = [
  [Video, "Online doctor consultations", "Video or audio consults with verified doctors, in IST slots that suit you.", "#2412B5"],
  [Pill, "Home medicine delivery", "Order prescribed medicines from nearby partner pharmacies, delivered home.", "#086B2D"],
  [FlaskConical, "Home sample collection", "Choose your lab and get samples collected at home.", "#086477"],
  [ScanLine, "Radiology appointments", "Book X-ray, ultrasound, CT or MRI slots at diagnostic centres in advance.", "#2412B5"],
  [FolderHeart, "Free health log", "Upload and keep every report date-wise, download anytime, share with your doctor.", "#086B2D"],
  [RefreshCcw, "Free 14-day follow-up", "Every prescribed consultation includes a free follow-up with the same doctor.", "#086477"],
];
const TICKER = ["Nirogyam AI report simplifier", "Verified doctors", "Nearest partners first", "Razorpay secure payments", "Digital prescriptions", "Unique Nirogyam Patient ID", "Home sample collection", "Medicine delivery", "Radiology booking", "Ratings from real customers"];

const STEPS = [
  [Search, "Find a doctor", "Filter verified doctors by specialty, mode, fee and availability."],
  [CalendarCheck, "Book a slot", "Pick a time in IST, share your concern and reports, and confirm."],
  [Video, "Consult online", "Join a private video or audio room from your phone or computer."],
  [FileText, "Get your prescription", "Receive a doctor-authorised e-prescription and follow-up plan."],
];
const BENEFITS = [
  [Clock, "No travel, no waiting rooms", "Consult from home at a time that suits you."],
  [ShieldCheck, "Private by design", "Records are private and shared only with your treating doctor."],
  [Wallet, "Transparent pricing", "See the full fee and refund policy before you book."],
  [HeartPulse, "Continuity of care", "Notes, prescriptions and follow-ups in one place."],
];
const FAQS = [
  ["What is Nirogyam AI?", "Nirogyam AI explains your uploaded reports in very simple language with suggested next steps. It is for understanding only and does not replace your doctor."],
  ["Who are the doctors on Nirogyam Teleclinic?", "Every doctor submits medical council registration and credentials, which are checked by our team before they can consult."],
  ["Is teleconsultation suitable for emergencies?", "No. For chest pain, breathing difficulty, severe injury or any emergency, call 112 or visit the nearest hospital immediately."],
  ["How do I share my reports?", "Upload PDFs or images to Health Records and attach them to a booking. Only the doctor you consult can view them."],
  ["Can I cancel or reschedule?", "Yes, from your appointment page. Refund eligibility depends on how early you cancel; the exact policy is shown before you confirm."],
  ["Will my consultation be recorded?", "No. Consultations are not recorded."],
  ["How do I receive my prescription?", "After the consultation the doctor authorises an e-prescription, which you can view and download as a PDF."],
];

const FEATURES = [
  { icon: Video, t: "Get a consultation", to: "/doctors", d: "Video or audio with verified doctors", c: "#2412B5" },
  { icon: Pill, t: "Medicines at home", to: "/patient/suvidha?category=pharmacy", d: "Order from your prescription in one tap", c: "#086B2D" },
  { icon: FlaskConical, t: "Free home sample collection", to: "/patient/suvidha?category=lab", d: "From the lab of your choice", c: "#086477" },
  { icon: ScanLine, t: "Book radiology in advance", to: "/patient/suvidha?category=radiology", d: "X-ray, USG, CT & MRI slots", c: "#2412B5" },
  { icon: FolderHeart, t: "Free health log", to: "/patient/records", d: "Reports stored date-wise, always yours", c: "#086B2D" },
  { icon: RefreshCcw, t: "Free 14-day follow-up", to: "/patient/appointments", d: "Included after a prescribed consult", c: "#086477" },
  { icon: Sparkles, t: "Nirogyam AI", to: "/patient/records", d: "Your reports explained in simple words", c: "#2412B5" },
  { icon: FileText, t: "Digital prescriptions", to: "/patient/records", d: "Doctor-signed PDF, anytime", c: "#2412B5" },
];

function FeatureCard({ offset, className, testId }) {
  const nav = useNavigate();
  const [n, setN] = useState(offset);
  useEffect(() => { const t = setInterval(() => setN((x) => (x + 1) % FEATURES.length), 3200); return () => clearInterval(t); }, []);
  const f = FEATURES[n];
  return (
    <div className={`absolute float-y ${className}`} data-testid={testId}>
      <button type="button" key={n} onClick={() => nav(f.to)} className="feat-swap surface w-60 p-4 text-left shadow-[0_20px_50px_-20px_rgba(36,18,181,0.3)] flex gap-3 items-start transition-transform hover:scale-[1.04] hover:border-[#086B2D]/40" data-testid={`${testId}-link`}>
        <span className="h-10 w-10 shrink-0 rounded-xl flex items-center justify-center" style={{ background: `${f.c}14`, color: f.c }}><f.icon className="h-5 w-5" /></span>
        <div><p className="text-sm font-semibold leading-snug">{f.t}</p><p className="text-xs muted mt-0.5">{f.d}</p></div>
      </button>
    </div>
  );
}

function FeatureStrip() {
  const nav = useNavigate();
  return (
    <div className="lg:hidden mt-10 -mx-5 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]" data-testid="hero-feature-strip">
      <div className="flex w-max marquee gap-3">{[...FEATURES, ...FEATURES].map((f, i) => (
        <button type="button" key={i} onClick={() => nav(f.to)} className="surface px-3 py-2.5 flex items-center gap-2 whitespace-nowrap text-sm active:scale-95 transition-transform" data-testid={`hero-strip-${i}`}><f.icon className="h-4 w-4" style={{ color: f.c }} />{f.t}</button>))}</div>
    </div>
  );
}

export default function Landing() {
  const nav = useNavigate();
  const [f, setF] = useState(emptyFilters);
  const [specs, setSpecs] = useState([]);
  useEffect(() => { api.get("/specialties").then((r) => setSpecs(r.data)).catch(() => {}); }, []);
  const search = () => nav(`/doctors?${new URLSearchParams(Object.entries({ ...f, q: "" }).filter(([, v]) => v && v !== "any")).toString()}`);
  return (
    <PublicShell>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-dots opacity-60 [mask-image:linear-gradient(to_bottom,black,transparent)]" />
        <FluidBackdrop />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-16 sm:pt-24 pb-16 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 min-w-0">
            <p className="eyebrow fade-up">Nirogyam Teleclinic · Bhubaneswar</p>
            <div className="mt-4 fade-up"><TypeHeadline /></div>
            <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.05] text-[#17232E] fade-up delay-1" data-testid="hero-headline">
              Better Care. <span className="text-[#2412B5]">Better Health.</span> <span className="text-[#086B2D]">Together.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg muted max-w-xl fade-up delay-2" data-testid="hero-subtext">
              Connect with trusted doctors from the comfort of your home. Book consultations, share medical reports, and manage your healthcare in one simple place.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 fade-up delay-3">
              <Button size="lg" className="rounded-full h-12 px-7" onClick={() => nav("/doctors")} data-testid="landing-hero-consult-button">Consult a Doctor <ArrowRight className="h-4 w-4 ml-2" /></Button>
              <Button size="lg" variant="outline" className="rounded-full h-12 px-7" onClick={() => nav("/auth")} data-testid="landing-hero-signin-button">Sign In</Button>
              <Button size="lg" variant="ghost" className="rounded-full h-12 px-6 text-[#2412B5] hover:bg-[#2412B5]/[0.06]" onClick={() => nav("/download")} data-testid="landing-hero-download-button"><Smartphone className="h-4 w-4 mr-2" />Download App</Button>
            </div>
            <FeatureStrip />
          </div>
          <div className="lg:col-span-5 relative hidden lg:flex justify-center">
            <div className="relative h-80 w-80">
              <div className="absolute inset-0 rounded-full border border-[#086B2D]/30 pulse-ring" />
              <div className="absolute inset-6 rounded-full border border-[#2412B5]/15" />
              <div className="absolute inset-16 rounded-full bg-white border border-[#E2E8ED] flex items-center justify-center"><img src="/icon-192.png" alt="" className="h-28 w-28" /></div>
              <FeatureCard offset={0} className="-bottom-8 -left-20" testId="hero-feature-card-1" />
              <FeatureCard offset={2} className="-top-6 -right-16 [animation-delay:1.2s]" testId="hero-feature-card-2" />
              <FeatureCard offset={4} className="top-[38%] -left-36 [animation-delay:2.4s] hidden xl:block" testId="hero-feature-card-3" />
            </div>
          </div>
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pb-20">
          <div className="surface p-4 sm:p-5 shadow-[0_24px_60px_-30px_rgba(23,35,46,0.25)]" data-testid="landing-doctor-search">
            <SearchFilters value={f} onChange={setF} onSubmit={search} compact />
          </div>
        </div>
      </section>

      <div className="border-y border-[#E2E8ED] bg-white overflow-hidden py-4" data-testid="landing-ticker">
        <div className="flex w-max marquee gap-10 text-sm text-[#17232E]/70">{[...TICKER, ...TICKER].map((t, i) => <span key={i} className="flex items-center gap-2 whitespace-nowrap"><span className="h-1.5 w-1.5 rounded-full bg-[#086B2D]" />{t}</span>)}</div>
      </div>

      <AiShowcase />
      <JointPromo />

      <section id="services" className="relative mx-auto max-w-7xl px-5 sm:px-8 py-20" data-testid="services-section">
        <Reveal><p className="eyebrow">Everything in one place</p><h2 className="mt-3 text-2xl sm:text-3xl font-semibold max-w-2xl">Consult, order, test and track your health – from home.</h2></Reveal>
        <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map(([Icon, t, d, c], i) => (
            <Reveal key={t} delay={i * 80}>
              <div className="surface p-6 lift h-full group" data-testid={`service-${i}`}>
                <div className="h-11 w-11 rounded-2xl flex items-center justify-center transition-transform duration-500 group-hover:rotate-6 group-hover:scale-110" style={{ background: `${c}14` }}><Icon className="h-5 w-5" style={{ color: c }} /></div>
                <h3 className="mt-5 font-semibold">{t}</h3><p className="mt-2 text-sm muted">{d}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={200}>
          <div className="mt-10 relative overflow-hidden rounded-3xl bg-[#2412B5] text-white p-8 sm:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6" data-testid="partner-cta">
            <FluidBackdrop className="opacity-60" />
            <div className="relative"><p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">For pharmacies, labs, imaging centres & more</p>
              <p className="mt-2 text-xl font-semibold">List your pharmacy, lab or service on Nirogyam.</p><p className="text-sm text-white/75 mt-1">List unlimited services free. Reach patients nearest to you first.</p></div>
            <Button size="lg" className="relative rounded-full bg-[#FFFFFF] text-[#2412B5] hover:bg-[#FFFFFF]/90" onClick={() => nav("/partners")} data-testid="landing-partner-btn"><Store className="h-4 w-4 mr-2" />Open Partner Portal</Button>
          </div>
        </Reveal>
      </section>

      <SuvidhaPromo />
      <PartnerLogoStrip />
      <LabBooking />

      <section id="how" className="bg-[#F7F9FA] border-y border-[#E2E8ED]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <p className="eyebrow">How it works</p>
          <h2 className="mt-3 text-2xl sm:text-3xl font-semibold max-w-xl">From sign-in to prescription in four calm steps.</h2>
          <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(([Icon, t, d], i) => (
              <Reveal key={t} delay={i * 100}><div className="surface p-6 lift h-full" data-testid={`how-step-${i + 1}`}>
                <div className="flex items-center justify-between"><Icon className="h-5 w-5 text-[#086B2D]" /><span className="font-display text-sm text-[#2412B5]/40">0{i + 1}</span></div>
                <h3 className="mt-6 font-semibold">{t}</h3><p className="mt-2 text-sm muted">{d}</p>
              </div></Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20" data-testid="specialties-section">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div><p className="eyebrow">Specialties</p><h2 className="mt-3 text-2xl sm:text-3xl font-semibold">Care across everyday health needs</h2></div>
          <Button variant="ghost" onClick={() => nav("/doctors")} className="rounded-full self-start">View all doctors <ArrowRight className="h-4 w-4 ml-1" /></Button>
        </div>
        <div className="mt-10 flex flex-wrap gap-2">
          {specs.map((s) => (
            <button key={s.id} onClick={() => nav(`/doctors?specialty=${s.id}`)} data-testid={`specialty-chip-${s.id}`}
              className="rounded-full border border-[#E2E8ED] px-4 py-2 text-sm hover:border-[#2412B5] hover:text-[#2412B5] transition-colors"><Stethoscope className="inline h-3.5 w-3.5 mr-1.5 text-[#086477]" />{s.name}</button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-20 grid lg:grid-cols-2 gap-6">
        <div className="surface p-8">
          <p className="eyebrow">Why consult online</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            {BENEFITS.map(([Icon, t, d]) => <div key={t}><Icon className="h-5 w-5 text-[#2412B5]" /><p className="mt-3 font-medium">{t}</p><p className="mt-1 text-sm muted">{d}</p></div>)}
          </div>
        </div>
        <div className="surface p-8 bg-[#086477] border-[#086477] text-white">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/70">Reports & prescriptions</p>
          <h3 className="mt-4 text-xl font-semibold">Share reports before you consult. Get your prescription right after.</h3>
          <ul className="mt-6 space-y-4 text-sm text-white/85">
            <li className="flex gap-3"><Upload className="h-4 w-4 mt-0.5 shrink-0" />Upload lab reports, scans and old prescriptions (PDF/JPG/PNG) to private Health Records.</li>
            <li className="flex gap-3"><ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />Attach them to a booking – only your treating doctor can open them, through short-lived secure links.</li>
            <li className="flex gap-3"><FileText className="h-4 w-4 mt-0.5 shrink-0" />Download a trusted, doctor-authorised e-prescription PDF and follow-up plan.</li>
          </ul>
        </div>
      </section>

      <section id="about" className="bg-[#F7F9FA] border-y border-[#E2E8ED]">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <p className="eyebrow">About Nirogyam</p>
            <h2 className="mt-3 text-2xl sm:text-3xl font-semibold">Accessible, dependable, technology-enabled healthcare.</h2>
            <p className="mt-4 text-sm muted leading-relaxed">Nirogyam Diagnostics and Healthcare Pvt Ltd is a Bhubaneswar-based healthcare company bringing diagnostics and doctor consultations closer to every home in Odisha. Nirogyam Teleclinic is our online consultation service.</p>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-4">
            {[[FlaskConical, "Diagnostics", "Laboratory tests and health check-ups through Nirogyam diagnostic services."], [Microscope, "Report review", "Discuss your diagnostic reports with a doctor online."], [Stethoscope, "Teleconsultation", "Video and audio consultations with verified doctors."]].map(([Icon, t, d]) => (
              <div key={t} className="surface p-6"><Icon className="h-5 w-5 text-[#086B2D]" /><p className="mt-4 font-medium">{t}</p><p className="mt-1 text-sm muted">{d}</p></div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 sm:px-8 py-20" data-testid="faq-section">
        <p className="eyebrow text-center">FAQs</p>
        <h2 className="mt-3 text-2xl sm:text-3xl font-semibold text-center">Questions, answered</h2>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map(([q, a], i) => (
            <AccordionItem key={q} value={`f${i}`}><AccordionTrigger data-testid={`faq-${i}`} className="text-left">{q}</AccordionTrigger><AccordionContent className="muted">{a}</AccordionContent></AccordionItem>
          ))}
        </Accordion>
      </section>
    </PublicShell>
  );
}
